import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import AuthForm from '@shared/components/AuthForm';
import Button from '@shared/components/Button';
import { InputPassword } from '@shared/components/InputPassword';
import Layout from '@shared/components/Layout';
import { usePublishScreenViewedEvent } from '@shared/utils/analytics/hooks';
import bannerLoginImage from '../universal-login/images/banner-login-desktop.webp';

export type ResetPasswordScreenMode =
  | 'form'
  | 'success'
  | 'expired'
  | 'invalid'
  | 'technicalError';

interface ResetPasswordScreenProps {
  screen: ResetPasswordScreenMode;
  serverErrorText?: string;
  onSubmit: (password: string) => Promise<void>;
  onRequestNewLink: () => void | Promise<void>;
  onBackToLogin: () => void | Promise<void>;
  resetError: () => void;
}

const MIN_PASSWORD_LENGTH = 8;
const RESET_PASSWORD_SCREEN_TITLE = 'Log In - Reset Password';
const recoveryLayoutProps = {
  variant: 'banner',
  bannerImageDesktop: bannerLoginImage,
  subHeadingClasses: 'mx-auto max-w-[420px]',
} as const;

const ResetPasswordScreen: React.FC<ResetPasswordScreenProps> = ({
  screen,
  serverErrorText,
  onSubmit,
  onRequestNewLink,
  onBackToLogin,
  resetError,
}) => {
  const { t } = useTranslation();
  usePublishScreenViewedEvent(RESET_PASSWORD_SCREEN_TITLE);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isRequestingNewLink, setIsRequestingNewLink] = useState(false);
  const [isReturningToLogin, setIsReturningToLogin] = useState(false);
  const trimmedPassword = password.trim();

  const passwordError =
    password.length > 0 && trimmedPassword.length < MIN_PASSWORD_LENGTH
      ? t('Password must be at least 8 characters')
      : serverErrorText;
  const confirmPasswordError =
    confirmPassword.length > 0 && password !== confirmPassword
      ? t('Passwords do not match.')
      : undefined;
  const canSubmit =
    trimmedPassword.length >= MIN_PASSWORD_LENGTH &&
    confirmPassword.length > 0 &&
    password === confirmPassword &&
    !isSubmitting;

  const handlePasswordChange = (value: string) => {
    resetError();
    setPassword(value);
  };

  const handleConfirmPasswordChange = (value: string) => {
    resetError();
    setConfirmPassword(value);
  };

  const handleSubmit = async () => {
    if (!canSubmit) {
      return;
    }

    setIsSubmitting(true);

    try {
      await onSubmit(trimmedPassword);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleRequestNewLink = async () => {
    setIsRequestingNewLink(true);

    try {
      await onRequestNewLink();
    } finally {
      setIsRequestingNewLink(false);
    }
  };

  const handleBackToLogin = async () => {
    setIsReturningToLogin(true);

    try {
      await onBackToLogin();
    } finally {
      setIsReturningToLogin(false);
    }
  };

  if (screen === 'form') {
    return (
      <Layout
        {...recoveryLayoutProps}
        heading={t('Reset password')}
        subHeading={t('Enter your new password.')}
      >
        <AuthForm>
          <InputPassword
            label={t('New password')}
            name="password"
            value={password}
            autoComplete="new-password"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              handlePasswordChange(event.target.value);
            }}
            onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
              if (event.key === 'Enter' && canSubmit) {
                event.preventDefault();
                void handleSubmit();
              }
            }}
            errorMessage={passwordError}
            state={passwordError ? 'error' : 'default'}
          />
          <InputPassword
            label={t('Confirm password')}
            name="confirmPassword"
            value={confirmPassword}
            autoComplete="new-password"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
              handleConfirmPasswordChange(event.target.value);
            }}
            onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
              if (event.key === 'Enter' && canSubmit) {
                event.preventDefault();
                void handleSubmit();
              }
            }}
            errorMessage={confirmPasswordError}
            state={confirmPasswordError ? 'error' : 'default'}
          />
          <Button
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
              event.preventDefault();
              void handleSubmit();
            }}
            variant="primary"
            disabled={!canSubmit}
            label={t('Reset password')}
            state={isSubmitting ? 'loading' : 'default'}
          />
        </AuthForm>
      </Layout>
    );
  }

  const screenCopy: Record<
    Exclude<ResetPasswordScreenMode, 'form'>,
    {
      heading: string;
      subHeading: string;
      requestNewLink?: boolean;
      backToLoginVariant?: 'primary' | 'outline';
    }
  > = {
    success: {
      heading: t('Password reset successful'),
      subHeading: t(
        'Your password has been updated. You can now log in with your new password.'
      ),
      backToLoginVariant: 'primary',
    },
    expired: {
      heading: t('This reset link has expired'),
      subHeading: t('Request a new password reset email to continue.'),
      requestNewLink: true,
      backToLoginVariant: 'outline',
    },
    invalid: {
      heading: t('This reset link is invalid'),
      subHeading: t('Request a new password reset email to continue.'),
      requestNewLink: true,
      backToLoginVariant: 'outline',
    },
    technicalError: {
      heading: t('Something went wrong'),
      subHeading: t('Please refresh the page and try again.'),
      backToLoginVariant: 'primary',
    },
  };
  const copy = screenCopy[screen];

  return (
    <Layout
      {...recoveryLayoutProps}
      heading={copy.heading}
      subHeading={copy.subHeading}
    >
      <div className="space-y-4">
        {copy.requestNewLink && (
          <Button
            onClick={() => {
              void handleRequestNewLink();
            }}
            variant="primary"
            label={t('Request a new link')}
            state={isRequestingNewLink ? 'loading' : 'default'}
          />
        )}
        <Button
          onClick={() => {
            void handleBackToLogin();
          }}
          variant={copy.backToLoginVariant ?? 'primary'}
          label={t('Back to log in')}
          state={isReturningToLogin ? 'loading' : 'default'}
        />
      </div>
    </Layout>
  );
};

export default ResetPasswordScreen;
